<template>
  <div class="file-upload-wrapper">
    <v-card
        :class="['file-upload-card', { 'drag-over': isDragging, 'has-file': hasFile, 'error': hasError }]"
        outlined
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
    >
      <v-card-text class="text-center pa-0">
        <!-- Área de Upload -->
        <div v-if="!hasFile" class="upload-area py-8">
          <v-icon size="64" :color="isDragging ? 'primary' : 'grey'">
            mdi-cloud-upload-outline
          </v-icon>

          <p class="mt-4 mb-2 text-subtitle-1 font-weight-medium">
            {{ isDragging ? 'Solte o arquivo aqui' : 'Arraste o arquivo ou clique para selecionar' }}
          </p>

          <p class="text-caption grey--text mb-4">
            Tipos permitidos: {{ tiposPermitidosTexto }}
            <br>
            Tamanho máximo: {{ tamanhoMaximoMB }}MB
          </p>

          <v-btn
              color="primary"
              outlined
              @click="openFileDialog"
              :disabled="disabled"
          >
            <v-icon left>mdi-file-search</v-icon>
            Selecionar Arquivo
          </v-btn>

          <input
              ref="fileInput"
              type="file"
              :accept="acceptTypes"
              @change="handleFileSelect"
              style="display: none"
          />
        </div>

        <!-- Preview do Arquivo -->
        <div v-else class="file-preview">
          <!-- Preview de PDF com VuePdfEmbed -->
          <div v-if="isPDF && showPDFPreview" class="pdf-preview-container">
            <!-- Miniatura do PDF (clicável) -->
            <v-row no-gutters>
              <v-col cols="12">
                <div class="pdf-thumbnail-wrapper elevation-3">
                  <!-- Overlay com informações do arquivo -->
                  <div class="pdf-info-overlay">
                    <div class="file-info-header">
                      <v-icon :color="getFileIcon().color" size="28" class="mr-2">
                        {{ getFileIcon().icon }}
                      </v-icon>
                      <div class="file-details-compact">
                        <p class="mb-0 text-subtitle-2 font-weight-medium white--text">
                          {{ fileName }}
                        </p>
                        <div class="d-flex align-center mt-1">
                          <span class="text-caption white--text">
                            {{ formatFileSize(fileSize) }}
                          </span>
                          <span v-if="pdfPageCount" class="text-caption white--text ml-2">
                            • {{ pdfPageCount }} pág{{ pdfPageCount > 1 ? 's' : '' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Hover overlay com botões de ação melhorados -->
                  <div class="pdf-hover-overlay">
                    <div class="hover-content">
                      <div class="hover-icon-wrapper">
                        <v-icon size="54" color="white" class="pulse-icon">
                          mdi-file-eye-outline
                        </v-icon>
                      </div>

                      <h3 class="white--text font-weight-bold text-h6 mb-2">
                        Visualizar Documento
                      </h3>
                      <p class="white--text text-body-2 mb-8 opacity-90">
                        Clique para expandir e navegar pelo PDF completo
                      </p>

                      <div class="overlay-actions">
                        <v-btn
                            color="white"
                            x-large
                            elevation="2"
                            @click.stop="toggleFullPreview"
                            :disabled="disabled"
                            class="action-btn primary-action"
                        >
                          <v-icon left size="24">mdi-arrow-expand-all</v-icon>
                          <span class="btn-text">Visualizar Completo</span>
                        </v-btn>

                        <v-btn
                            v-if="!disabled"
                            color="white"
                            x-large
                            elevation="2"
                            outlined
                            @click.stop="removeFile"
                            class="action-btn secondary-action"
                        >
                          <v-icon left size="24" color="error">mdi-delete-outline</v-icon>
                          <span class="btn-text error--text">Remover</span>
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <!-- PDF Embed (miniatura) -->
                  <div v-if="pdfDataUrl" class="pdf-thumbnail-content">
                    <VuePdfEmbed
                        :source="pdfDataUrl"
                        :page="1"
                        class="pdf-thumbnail"
                        @rendered="handlePdfRendered"
                    />
                  </div>

                  <div v-else class="pdf-loading">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        size="32"
                    ></v-progress-circular>
                    <p class="mt-2 text-caption grey--text">Gerando preview...</p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Preview padrão (outros tipos de arquivo) -->
          <div v-else class="default-preview">
            <v-icon size="48" :color="getFileIcon().color">
              {{ getFileIcon().icon }}
            </v-icon>

            <p class="mt-3 mb-1 text-subtitle-1 font-weight-medium">
              {{ fileName }}
            </p>

            <p class="text-caption grey--text mb-0">
              {{ formatFileSize(fileSize) }}
            </p>

            <v-btn
                v-if="!disabled"
                color="white"
                x-large
                elevation="2"
                outlined
                @click.stop="removeFile"
                class="action-btn secondary-action mt-2"
            >
              <v-icon left size="24" color="error">mdi-delete-outline</v-icon>
              <span class="btn-text error--text">Remover</span>
            </v-btn>
          </div>
        </div>

        <!-- Loading -->
        <v-overlay v-if="loading" absolute>
          <v-progress-circular
              indeterminate
              color="primary"
              size="48"
          ></v-progress-circular>
          <p class="mt-3 white--text">Processando arquivo...</p>
        </v-overlay>
      </v-card-text>
    </v-card>

    <!-- Mensagem de Erro -->
    <v-alert
        v-if="errorMessage"
        type="error"
        dense
        text
        class="mt-2 mb-0"
    >
      {{ errorMessage }}
    </v-alert>

    <!-- Label do Campo (opcional) -->
    <div v-if="label" class="mt-2">
      <span class="text-caption grey--text">{{ label }}</span>
      <span v-if="required" class="error--text ml-1">*</span>
    </div>

    <!-- Dialog de Preview Completo com iframe -->
    <v-dialog
        v-model="fullPreviewDialog"
        max-width="1400px"
        @click:outside="fullPreviewDialog = false"
    >
      <v-card>
        <v-card-title class="pdf-dialog-header pa-4 white--text">
          <v-icon color="red" class="mr-2">mdi-file-pdf-box</v-icon>
          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-medium">{{ fileName }}</div>
            <div class="text-caption grey--text">
              {{ formatFileSize(fileSize) }}
              <span v-if="pdfPageCount"> • {{ pdfPageCount }} página{{ pdfPageCount > 1 ? 's' : '' }}</span>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn
              icon
              @click="fullPreviewDialog = false"
              color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <iframe
              v-if="pdfDataUrlFull"
              :src="pdfDataUrlFull"
              class="pdf-viewer-full"
              frameborder="0"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'

export default {
  name: 'FileUpload',

  components: {
    VuePdfEmbed
  },

  props: {
    value: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tiposPermitidos: {
      type: Array,
      default: () => ['pdf', 'docx', 'doc', 'xlsx', 'xls']
    },
    tamanhoMaximoMB: {
      type: Number,
      default: 20
    },
    showPDFPreview: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isDragging: false,
      loading: false,
      errorMessage: '',
      fileName: '',
      fileSize: 0,
      fileBase64: '',
      fileType: '',
      pdfDataUrl: '',
      pdfDataUrlFull: '',
      fullPreviewDialog: false,
      pdfPageCount: 0
    }
  },

  computed: {
    hasFile() {
      return !!this.fileName
    },

    hasError() {
      return !!this.errorMessage
    },

    isPDF() {
      return this.fileType === 'pdf'
    },

    acceptTypes() {
      const mimeTypes = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
      return this.tiposPermitidos.map(tipo => mimeTypes[tipo] || '').filter(Boolean).join(',')
    },

    tiposPermitidosTexto() {
      return this.tiposPermitidos.map(tipo => tipo.toUpperCase()).join(', ')
    },

    tamanhoMaximoBytes() {
      return this.tamanhoMaximoMB * 1024 * 1024
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue.nome) {
          this.fileName = newValue.nome
          this.fileSize = newValue.tamanho || 0
          this.fileBase64 = newValue.base64 || ''
          this.fileType = newValue.tipo || ''

          // Gerar preview de PDF se necessário
          if (this.isPDF && this.fileBase64) {
            this.generatePDFPreview()
          }
        } else if (!newValue) {
          this.clearFile()
        }
      }
    }
  },

  methods: {
    openFileDialog() {
      if (!this.disabled) {
        this.$refs.fileInput.click()
      }
    },

    handleDragOver() {
      if (!this.disabled) {
        this.isDragging = true
      }
    },

    handleDragLeave() {
      this.isDragging = false
    },

    handleDrop(event) {
      this.isDragging = false
      if (this.disabled) return

      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.processFile(files[0])
      }
    },

    handleFileSelect(event) {
      const files = event.target.files
      if (files.length > 0) {
        this.processFile(files[0])
      }
    },

    async processFile(file) {
      this.errorMessage = ''
      this.loading = true

      try {
        // Validar extensão
        console.log(this.tiposPermitidos)

        const extensao = this.getFileExtension(file.name)
        console.log(extensao)
        if (!this.tiposPermitidos.includes(extensao)) {
          throw new Error(`Tipo de arquivo não permitido. Use: ${this.tiposPermitidosTexto}`)
        }

        // Validar tamanho
        if (file.size > this.tamanhoMaximoBytes) {
          throw new Error(`Arquivo muito grande. Tamanho máximo: ${this.tamanhoMaximoMB}MB`)
        }

        // Converter para base64
        const base64 = await this.fileToBase64(file)

        // Atualizar dados
        this.fileName = file.name
        this.fileSize = file.size
        this.fileBase64 = base64
        this.fileType = extensao

        // Gerar preview de PDF
        if (this.isPDF) {
          this.generatePDFPreview()
        }

        // Emitir evento
        this.$emit('input', {
          nome: this.fileName,
          tamanho: this.fileSize,
          base64: this.fileBase64,
          tipo: this.fileType
        })

        this.$emit('change', {
          nome: this.fileName,
          tamanho: this.fileSize,
          base64: this.fileBase64,
          tipo: this.fileType
        })

      } catch (error) {
        this.errorMessage = error.message
        this.$emit('error', error.message)
      } finally {
        this.loading = false
        // Limpar input para permitir selecionar o mesmo arquivo novamente
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
        }
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
          // Remove o prefixo "data:...;base64," do resultado
          const base64 = reader.result.split(',')[1]
          resolve(base64)
        }

        reader.onerror = () => {
          reject(new Error('Erro ao ler o arquivo'))
        }

        reader.readAsDataURL(file)
      })
    },

    generatePDFPreview() {
      if (!this.fileBase64) {
        this.pdfDataUrl = ''
        this.pdfDataUrlFull = ''
        return
      }

      try {
        // Data URL simples para VuePdfEmbed (miniatura)
        this.pdfDataUrl = `data:application/pdf;base64,${this.fileBase64}`

        // Data URL com parâmetros para iframe (visualização completa)
        const pdfParams = [
          'navpanes=1',
          'pagemode=thumbs',
          'page=1'
        ].join('&')

        this.pdfDataUrlFull = `data:application/pdf;base64,${this.fileBase64}#${pdfParams}`
      } catch (error) {
        console.error('Erro ao gerar preview do PDF:', error)
        this.pdfDataUrl = ''
        this.pdfDataUrlFull = ''
      }
    },

    handlePdfRendered(pdf) {
      // Captura o número total de páginas quando o PDF é renderizado
      if (pdf && pdf.numPages) {
        this.pdfPageCount = pdf.numPages
      }
    },

    toggleFullPreview() {
      this.fullPreviewDialog = true
    },

    removeFile() {
      this.clearFile()
      this.$emit('input', null)
      this.$emit('change', null)
      this.$emit('remove')
    },

    clearFile() {
      this.fileName = ''
      this.fileSize = 0
      this.fileBase64 = ''
      this.fileType = ''
      this.errorMessage = ''
      this.pdfDataUrl = ''
      this.pdfDataUrlFull = ''
      this.pdfPageCount = 0
    },

    getFileExtension(fileName) {
      return fileName.split('.').pop().toLowerCase()
    },

    getFileIcon() {
      const icons = {
        'pdf': { icon: 'mdi-file-pdf-box', color: 'red' },
        'doc': { icon: 'mdi-file-word-box', color: 'blue' },
        'docx': { icon: 'mdi-file-word-box', color: 'blue' },
        'xls': { icon: 'mdi-file-excel-box', color: 'green' },
        'xlsx': { icon: 'mdi-file-excel-box', color: 'green' }
      }
      return icons[this.fileType] || { icon: 'mdi-file-document', color: 'grey' }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.file-upload-wrapper {
  width: 100%;
}

.file-upload-card {
  transition: all 0.3s ease;
  border: 2px dashed #ccc;
  background-color: #fafafa;
  cursor: pointer;
  position: relative;
}

.file-upload-card.drag-over {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.05);
  transform: scale(1.02);
}

.file-upload-card.has-file {
  border-style: solid;
  border-radius: 12px;
  background-color: rgba(var(--v-success-base), 0.05);
  cursor: default;
}

.file-upload-card.error {
  border-color: var(--v-error-base);
  background-color: rgba(var(--v-error-base), 0.05);
}

.upload-area {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.file-preview {
  min-height: 180px;
}

.default-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* PDF Preview Styles */
.pdf-preview-container {
  width: 100%;
}

/* Miniatura do PDF */
.pdf-thumbnail-wrapper {
  position: relative;
  width: 100%;
  margin: 0 auto;
  height: 350px;
  border-radius: 12px;
  overflow: hidden;
  background-color: #ffffff;
  transition: all 0.3s ease;
}

.pdf-thumbnail-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
  border-color: #023449;
}

.pdf-thumbnail-wrapper:hover .pdf-hover-overlay {
  opacity: 1;
  backdrop-filter: blur(8px);
  border-radius: 12px;
}

/* Overlay de informações (sempre visível) */
.pdf-info-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, rgba(2, 52, 73, 0.95) 0%, rgba(2, 52, 73, 0.7) 75%, transparent 100%);
  padding: 20px 24px;
  z-index: 2;
  pointer-events: none;
}

.file-info-header {
  display: flex;
  align-items: flex-start;
}

.file-details-compact {
  min-width: 0;
}

.file-details-compact p {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

/* Overlay de hover melhorado */
.pdf-hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(2, 52, 73, 0.61) 0%, rgba(3, 77, 107, 0.94) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-content {
  text-align: center;
  padding: 0 32px;
  animation: fadeInUp 0.5s ease-out;
}

.hover-icon-wrapper {
  display: inline-block;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  margin-bottom: 24px;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.pulse-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.opacity-90 {
  opacity: 0.9;
}

.overlay-actions {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0 20px;
}

.action-btn {
  min-width: 200px;
  height: 46px !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s;
}

.action-btn:hover::before {
  left: 100%;
}

.primary-action {
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%) !important;
  color: #023449 !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
}

.primary-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3) !important;
}

.secondary-action {
  background: transparent !important;
  border: 2px solid white !important;
}

.secondary-action:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: translateY(-2px);
}

.btn-text {
  font-size: 15px;
  font-weight: 600;
}

.pdf-thumbnail-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
  background: #f5f5f5;
  padding-top: 70px;
}

.pdf-thumbnail {
  width: 50%;
  max-width: 75%;
  height: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.pdf-thumbnail-wrapper:hover .pdf-thumbnail {
  transform: scale(0.98);
}

.pdf-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f5f5;
}

/* Dialog Styles */
.pdf-dialog-header {
  background: rgba(2, 52, 73, 0.9);
  border-bottom: 1px solid whitesmoke;
}

.pdf-viewer-full {
  width: 100%;
  height: calc(100vh - 200px);
  min-height: 600px;
  background-color: #525659;
}

/* Responsivo */
@media (max-width: 960px) {
  .pdf-thumbnail-wrapper {
    height: 400px;
  }

  .pdf-thumbnail-content {
    padding-top: 70px;
  }

  .pdf-thumbnail {
    width: 90%;
    max-width: 90%;
  }

  .hover-icon-wrapper {
    padding: 16px;
    margin-bottom: 20px;
  }

  .pulse-icon {
    font-size: 56px !important;
  }

  .hover-content h3 {
    font-size: 1.25rem;
  }

  .action-btn {
    min-width: 180px;
    height: 42px !important;
  }
}

@media (max-width: 768px) {
  .pdf-thumbnail-wrapper {
    height: 380px;
  }

  .pdf-thumbnail-content {
    padding-top: 90px;
  }

  .pdf-thumbnail {
    width: 92%;
    max-width: 92%;
  }

  .pdf-viewer-full {
    height: 75vh;
    min-height: 500px;
  }

  .hover-content {
    padding: 0 24px;
  }

  .overlay-actions {
    flex-direction: column;
    gap: 12px;
  }

  .action-btn {
    width: 85%;
    max-width: 280px;
    min-width: unset;
  }
}

@media (max-width: 600px) {
  .pdf-thumbnail-wrapper {
    height: 350px;
  }

  .pdf-thumbnail {
    width: 95%;
    max-width: 95%;
  }

  .hover-icon-wrapper {
    padding: 12px;
    margin-bottom: 16px;
  }

  .pulse-icon {
    font-size: 48px !important;
  }

  .hover-content h3 {
    font-size: 1.1rem;
  }

  .hover-content p {
    font-size: 0.875rem;
    margin-bottom: 24px !important;
  }

  .action-btn {
    width: 90%;
    height: 40px !important;
  }

  .btn-text {
    font-size: 14px;
  }
}
</style>